/**
 * TargetGroupNode — Custom ReactFlow node that groups 8+ target tables
 * into a single compact, scrollable node on the canvas.
 *
 * Visual spec:
 * - Width 260px, light background, green left border
 * - Header: target icon + "Target Tables" + count badge
 * - Scrollable table list: table_name with status indicator (green/red)
 * - Summary: "X/Y materialized"
 * - Top Handle only (mapping_output → target group)
 */
import React from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { Box, Chip, Tooltip, Typography, useTheme, alpha } from '@mui/material';
import {
  TableChart as TargetIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';

export interface TargetTableEntry {
  name: string;
  viewName: string;
  status: 'completed' | 'failed' | 'pending' | 'running' | 'awaiting_review';
  ddl?: string;
  error?: string;
  /** Number of health issues from mapping health API. */
  healthIssueCount?: number;
  /** Worst severity among health issues. */
  healthSeverity?: 'error' | 'warning' | null;
}

export interface TargetGroupNodeData {
  tableCount: number;
  tables: TargetTableEntry[];
  /** Callback when user clicks a table name — opens inspector for that table. */
  onTableClick?: (tableName: string) => void;
}

const MAX_VISIBLE = 6;

const TargetGroupNode: React.FC<NodeProps<TargetGroupNodeData>> = ({
  data,
  selected,
  targetPosition,
}) => {
  const targetHandlePos = targetPosition ?? Position.Top;
  const theme = useTheme();
  const { tableCount, tables, onTableClick } = data;

  const completedCount = tables.filter(t => t.status === 'completed').length;
  const failedCount = tables.filter(t => t.status === 'failed').length;

  const visibleTables = tables.slice(0, MAX_VISIBLE);
  const remainingCount = tables.length - MAX_VISIBLE;

  const statusColor = failedCount > 0 ? '#ef4444' : completedCount === tableCount ? '#22c55e' : '#f59e0b';

  return (
    <Box
      sx={{
        width: 260,
        background: '#f8faf8',
        borderRadius: 0.5,
        border: selected
          ? `2px solid ${theme.palette.success.main}`
          : '1.5px solid #e2e8f0',
        borderLeft: `4px solid ${theme.palette.success.main}`,
        boxShadow: selected
          ? `0 0 0 3px ${alpha(theme.palette.success.main, 0.15)}`
          : '0 1px 3px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        cursor: 'pointer',
      }}
    >
      {/* Target handle — edge from mapping_output */}
      <Handle
        type="target"
        position={targetHandlePos}
        style={{
          background: theme.palette.success.main,
          width: 8,
          height: 8,
          border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <TargetIcon sx={{ fontSize: 20, color: theme.palette.success.main }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ fontSize: 13, lineHeight: 1.3 }}
          >
            Target Tables
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: 11 }}
          >
            {completedCount}/{tableCount} materialized
            {failedCount > 0 && ` · ${failedCount} failed`}
          </Typography>
        </Box>
        <Chip
          label={tableCount}
          size="small"
          sx={{
            fontSize: 10,
            height: 20,
            minWidth: 28,
            bgcolor: alpha(statusColor, 0.12),
            color: statusColor,
            fontWeight: 700,
          }}
        />
      </Box>

      {/* Table list */}
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          minHeight: 24,
          maxHeight: MAX_VISIBLE * 22 + 8,
          overflowY: 'auto',
        }}
      >
        {visibleTables.map((t) => {
          const StatusIconComponent =
            t.status === 'completed' ? SuccessIcon
            : t.status === 'failed' ? ErrorIcon
            : PendingIcon;
          const iconColor =
            t.status === 'completed' ? '#22c55e'
            : t.status === 'failed' ? '#ef4444'
            : '#94a3b8';

          return (
            <Box
              key={t.name}
              onClick={(e) => {
                e.stopPropagation();
                onTableClick?.(t.name);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                py: 0.25,
                cursor: 'pointer',
                borderRadius: 0.5,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
              }}
            >
              <StatusIconComponent sx={{ fontSize: 12, color: iconColor, flexShrink: 0 }} />
              <Tooltip title={t.error || t.name} placement="top" arrow disableInteractive>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontFamily: 'monospace',
                    color: t.status === 'failed' ? '#ef4444' : '#334155',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                  }}
                >
                  {t.name}
                </Typography>
              </Tooltip>
              {!!t.healthIssueCount && t.healthIssueCount > 0 && (
                <Tooltip title={`${t.healthIssueCount} issue${t.healthIssueCount !== 1 ? 's' : ''}`} placement="top" arrow disableInteractive>
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: t.healthSeverity === 'error' ? '#ef4444' : '#f59e0b',
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          );
        })}
        {remainingCount > 0 && (
          <Typography
            sx={{ fontSize: 10, color: '#94a3b8', mt: 0.25, fontStyle: 'italic' }}
          >
            + {remainingCount} more
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TargetGroupNode;
